<script setup lang="ts">
import type { ResolvedMonospacePrimitiveField } from '#layers/data-model/types/schema/resolved-schema';
import { computed, ref } from 'vue';
import { provideBlockInjection } from '#layers/block-engine';
import { injectFormBlockContext } from '~/composables/form-block/form-context';
import { useInputBlockDisabled } from '~/composables/form-block/use-base-form-field-state';
import { providePrimitiveFormFieldContext } from '~/composables/form-block/use-primitive-form-field';
import { useDisplayNames } from '~/composables/use-display-names';
import { getPrimitiveFieldInputBlockOptions } from '~/utils/fields/primitive-field';

const props = defineProps<{
	primitiveField?: ResolvedMonospacePrimitiveField;
	label?: string;
	description?: string;
	name?: string;
	required?: boolean;
	ejected?: boolean;
}>();

const { getPrimitiveFieldDisplayName } = useDisplayNames();
const label = computed(() => props.label ?? (props.primitiveField ? getPrimitiveFieldDisplayName(props.primitiveField) : ''));
const description = computed(() => props.description ?? props.primitiveField?.meta?.description ?? undefined);
const name = computed(() => props.name ?? props.primitiveField?.apiName);
const required = computed(() => props.required ?? !props.primitiveField?.isNullable);

const formBlock = injectFormBlockContext(null);

const fallbackModel = ref<unknown>();

const model = computed({
	get: () => name.value ? formBlock?.state.value?.[name.value] : fallbackModel.value,
	set: (value) => {
		if (name.value && formBlock?.state.value) {
			formBlock.state.value[name.value] = value;
		}
		else {
			fallbackModel.value = value;
		}
	},
});

const inputBlockProps = computed(() => {
	if (!props.primitiveField) {
		return {
			block: '$builtin:Input',
		};
	}

	return getPrimitiveFieldInputBlockOptions(props.primitiveField);
});

const inputBlockDisabled = useInputBlockDisabled(inputBlockProps);

const { disabled } = providePrimitiveFormFieldContext({
	model,
	field: () => props.primitiveField,
	disabled: inputBlockDisabled,
});

provideBlockInjection({
	model,
});

const showLabelDropdown = computed(() => !!props.primitiveField?.type);
</script>

<template>
	<DFormField
		:name
		:label
		:description
		:required
		:ui="{
			labelWrapper: showLabelDropdown ? 'pe-4' : undefined,
		}"
	>
		<!-- Dropdown trigger renders OUTSIDE the <Label> element via label-trailing slot -->
		<template v-if="showLabelDropdown" #label-trailing>
			<FormFieldLabelDropdown
				v-model="model"
				:label
				:primitive-field
				:disabled
			/>
		</template>
		<template v-if="!ejected && formBlock?.collection.value && inputBlockProps">
			<BlockRender v-bind="inputBlockProps" />
		</template>
		<slot v-else />
	</DFormField>
</template>
